import gsap from 'gsap';
import { Group, MeshPhongMaterial, Vector3 } from 'three';
import WebGLSketch from '../_app/cuchillo/3D/WebGLSketch';
import { GetBy } from '../_app/cuchillo/core/Element';
import { Metrics } from '../_app/cuchillo/core/Metrics';
import { Maths } from '../_app/cuchillo/utils/Maths';
import { Ease } from '../_app/cuchillo/utils/Ease';
import { Interaction } from '../_app/cuchillo/core/Interaction';
import { ControllerPage } from '../_app/cuchillo/pages/ControllerPage';
import ScrollItem__WidgetHome from '../scroll/ScrollItem__WidgetHome';
import Header from '../layout/Header';
import Scene from './Scene';
import Tapon from './Tapon';

const TAPON_MODEL = '/assets/models/tapon-new.glb';
const TAPON_COLS = 0.5;          // escala del tapon respecte l'amplada del DOM (tunejable)
const TAPON_SHININESS = 60;
const TAPON_COLORS = {
    luminaris: 0xF1F1F1,
    obscurios: 0x000000,
};

// Z dels tapones en funció de l'amplada de pantalla: z = zFactor * Metrics.WIDTH.
// 0.26 ≈ 500 a ~1920px d'amplada. Tunejable al debug pane.
export const TAPON_SETTINGS = {
    zFactor: 0.26,
};

// Animació d'entrada dels tapons (reproduïda del thumbnail principal de Product): entra des
// d'un offset Z + Y (anima a 0, EASE_CUCHILLO_OUT) + fade d'opacity. L'stagger entre tapons el
// gestiona ScrollItem__WidgetHome.
export const TAPON_SHOW_SETTINGS = {
    introZ: 4000,    // offset Z inicial → 0 (com el thumbnail de Product)
    introY: 1000,    // offset Y inicial → 0
    durationZ: 2.5,  // durada de l'anim Z
    durationY: 2,    // durada de l'anim Y
    durationX: 2.5,  // durada de l'entrada des del centre (X 0 → posició final)
    duration: 1,     // durada del fade d'opacity
    delay: 0,        // delay del fade d'opacity (l'stagger ve de WidgetHome)
};

// Hide dels tapons: MATEIXA sensació que el show però en revers i més frontal a la càmera.
// Tot es mou amb UN sol progrés (_outProgress 0→1), una sola durada i un sol ease (el mateix
// del show) → X, Y i Z viatgen junts = una paràbola coherent (sense salts ni timings dispars):
// - X → Xoriginal × outXFactor (el factor depèn de si el tapon és el clicat o no).
// - Y → outYFactor × HEIGHT (puja).
// - Z → camZ × outZTargetFactor (Z ABSOLUTA relativa a la càmera; sempre la passa i surt del
//   frustum, independentment de l'amplada → funciona igual a mobile, on z_base és petit).
// - fade out d'opacity a prop de la càmera (de camZ×fadeStartFactor fins a camZ×fadeEndFactor) per
//   evitar el glitch de render quan el tapon s'hi acosta massa. Tot proporcional a la Z de la càmera.
// Mantenim delays/staggers; l'stagger general el gestiona el caller. Tot tunejable.
export const TAPON_HIDE_SETTINGS = {
    outXFactorOther: 0.2,    // X de sortida del tapon NO clicat = Xoriginal × 0.2
    outXFactorClicked: 0.02, // X de sortida del tapon CLICAT = Xoriginal × 0.02 (més cap al centre)
    outYFactor: 0.1,     // Y de sortida = 0.1 × HEIGHT (amunt)
    outZTargetFactor: 1.2, // Z final = camZ × aquest factor (~1800 si camZ=1500): passa la càmera i surt
    fadeStartFactor: 1300 / 1500, // fade out: comença a camZ × aquest factor (~1300 si camZ=1500)
    fadeEndFactor: 1400 / 1500,   // fade out: acaba a camZ × aquest factor (~1400 si camZ=1500)
    duration: 2,         // UNA durada per a tots els eixos (moviment coherent, com el show)
    stagger: 0.3,        // stagger entre tapons al hide general (igual que WidgetHome)
    ease: Ease.EASE_CUCHILLO_OUT, // MATEIX ease que el show
};

// Rotació inicial Y (graus) per tapon, tunejable al debug pane. +90 = frontal.
export const TAPON_INIT_ROT = {
    luminaris: 25,
    obscurios: -25,
};

// Rotació seguint el mouse a tota la pantalla (relativa al centre del DOM de cada tapon).
// rotX/rotY = graus màx (range simètric ±valor). Eix X ← Y mouse, eix Y ← X mouse.
export const TAPON_MOUSE_SETTINGS = {
    rotX: 0.025, // intensitat rot X (eix vertical): graus per píxel de distància mouseY↔centre
    rotY: 0.045, // intensitat rot Y (eix horitzontal): graus per píxel de distància mouseX↔centre
    lerp: 0.06,
};

const _projPos = new Vector3(); // temp per projectar el centre 3D del tapon a pantalla

export default class HomeScene {
    static group = new Group();
    static _tapones = [];
    static _taponByType = {}; // { luminaris: Tapon, obscurios: Tapon } per al lookAt de llums
    static _taponSelected = false;

    static init(container) {
        if (!WebGLSketch.renderer) WebGLSketch.init();
        WebGLSketch.scene.add(this.group);

        this._tapones = [];
        this._taponByType = {};
        this._taponSelected = false; // reset en re-entrar a Home (sinó els clics queden bloquejats)

        const els = [...GetBy.class('__tapon', container)];
        for (let i = 0; i < els.length; i++) {
            const el = els[i];
            const type = el.dataset.type || 'luminaris';
            const material = new MeshPhongMaterial({
                color: TAPON_COLORS[type] ?? TAPON_COLORS.luminaris,
                shininess: TAPON_SHININESS,
            });

            const tapon = new Tapon({
                dom: el,
                model: TAPON_MODEL,
                material,
                hasFlotation: true,
                cols: TAPON_COLS,
            });

            // Estat inicial: offset d'entrada Z + Y (anima a 0) + opacity 0
            tapon._showProxy = { y: 0, o: 0 };
            tapon._showY = 0;
            tapon._introZ = TAPON_SHOW_SETTINGS.introZ;
            tapon._introY = TAPON_SHOW_SETTINGS.introY;
            tapon._introXFactor = 0; // comença al centre (X=0) → 1 = posició final
            tapon._outProgress = 0;  // 0 = posició normal; el hide el deriva a la sortida (→ 1)

            // Destí del click: data-url del target (fallbacks: data-src, href)
            const src = el.dataset.url || el.dataset.src || el.getAttribute('href') || '';

            tapon._type = type; // per al lookup de rotació inicial / lookAt de llums

            tapon.init().then(() => {
                Scene.setProductLayer(tapon, type); // layer segons tipus: luminaris(4) / obscurios(5)
                this.group.add(tapon);
                tapon.resize();
                tapon.setOpacity(0);

                // Raycaster sobre el proxy (caixa lleugera), no la geometria pesada.
                // isClickable arrenca a false; s'activa quan acaba l'animació de show
                // (evita clicks durant preloader/animacions, com a la resta del projecte).
                if (tapon._proxy) {
                    tapon._proxy.userData.isClickable = false;
                    tapon._proxy.userData.onClick = () => this.onTaponClick(i, src, type);
                    WebGLSketch.addInteractive(tapon._proxy);
                }

                tapon.active = true;
            });

            this._tapones.push(tapon);
            this._taponByType[type] = tapon; // luminaris / obscurios → lookAt de la seva família
        }
    }

    // Show del tapon (cridat des del button-torcido, staggered per WidgetHome): entrada com el
    // thumbnail principal de Product → offset Z + Y (anima a 0, EASE_CUCHILLO_OUT) + fade d'opacity.
    static showTapon(index) {
        const tapon = this._tapones[index];
        if (!tapon) return;
        const p = tapon._showProxy;
        gsap.killTweensOf(p);
        gsap.killTweensOf(tapon); // tweens d'_introZ / _introY
        p.y = 0; tapon._showY = 0;

        this.updateLightTargets(); // apunta la llum de la família al centre del tapon (un cop)

        // Z + Y des de l'offset → 0, i X des del centre (factor 0 → 1). Ease del thumbnail de Product.
        tapon._introZ = TAPON_SHOW_SETTINGS.introZ;
        tapon._introY = TAPON_SHOW_SETTINGS.introY;
        tapon._introXFactor = 0;
        tapon._outProgress = 0; // reset del hide (per si es re-mostra després d'un out)
        gsap.to(tapon, { _introZ: 0, duration: TAPON_SHOW_SETTINGS.durationZ, ease: Ease.EASE_CUCHILLO_OUT });
        gsap.to(tapon, { _introY: 0, duration: TAPON_SHOW_SETTINGS.durationY, ease: Ease.EASE_CUCHILLO_OUT });
        gsap.to(tapon, { _introXFactor: 1, duration: TAPON_SHOW_SETTINGS.durationX, ease: Ease.EASE_CUCHILLO_OUT });

        // Fade d'opacity
        gsap.to(p, {
            o: 1, duration: TAPON_SHOW_SETTINGS.duration, delay: TAPON_SHOW_SETTINGS.delay,
            onUpdate: () => { tapon.setOpacity(p.o); },
            onComplete: () => { this._setClickable(tapon, true); } // clicable quan acaba l'animació
        });
    }

    static _setClickable(tapon, value) {
        if (tapon._proxy) tapon._proxy.userData.isClickable = value;
    }

    // Progrés d'ENTRADA del helmet (tapon) mapejat a l'scroll: p 0→1.
    //   p=0 → estat inicial (offset Z+Y gran + centrat a X → queda darrere la càmera / fora de pla)
    //   p=1 → posició final (Z/Y a repòs, X lateral)
    // L'entrada és NOMÉS de POSICIÓ (sense fade): l'opacitat es manté plena; a p=0 el gran offset Z
    // (~4000) el posa darrere la càmera, així que no cal animar l'opacitat.
    // Reversible: scroll amunt/avall el recalcula. Ho controla ScrollItem__WidgetHome via HelmetHolder.
    // El bucle (loop) ja llegeix _introZ/_introY/_introXFactor cada frame per posicionar el tapon.
    static setTaponProgress(index, p) {
        const tapon = this._tapones[index];
        if (!tapon) return;
        tapon._outProgress = 0; // no estem en hide (el hide del click té el seu propi progrés)
        tapon._introZ = (1 - p) * TAPON_SHOW_SETTINGS.introZ;
        tapon._introY = (1 - p) * TAPON_SHOW_SETTINGS.introY;
        tapon._introXFactor = p;
        if (tapon.active) tapon.setOpacity(1); // opacitat plena: l'entrada la fa la posició, no el fade
        this._setClickable(tapon, p >= 0.999); // clicable només quan està del tot mostrat
    }

    // Reset del tapon a l'estat inicial (amagat: a la seva posició + opacity 0)
    static hideTapon(index) {
        const tapon = this._tapones[index];
        if (!tapon) return;
        const p = tapon._showProxy;
        gsap.killTweensOf(p);
        gsap.killTweensOf(tapon);
        p.y = 0;
        p.o = 0;
        tapon._showY = 0;
        tapon._introZ = TAPON_SHOW_SETTINGS.introZ;
        tapon._introY = TAPON_SHOW_SETTINGS.introY;
        tapon._introXFactor = 0;
        tapon._outProgress = 0;
        tapon.setOpacity(0);
        this._setClickable(tapon, false); // no clicable mentre està amagat
    }

    static hideTapones() {
        this._tapones.forEach((_, i) => this.hideTapon(i));
    }

    // Hide general de la pàgina ("hide normal"): els tapons marxen COM HAN ARRIBAT
    // (revers del show, Z + Y) amb el mateix stagger que l'entrada. No s'usa quan el hide
    // ve d'un click de tapon (onTaponClick ja gestiona el seu propi ordre/stagger).
    static hideTaponesAnimated() {
        this._tapones.forEach((_, i) => {
            gsap.delayedCall(i * TAPON_HIDE_SETTINGS.stagger, () => this.hideTaponAnimated(i));
        });
    }

    // Click d'un __tapon: amaga buttons + tapones (no-clicat primer, clicat +0.5s),
    // llança l'animació general i, en acabar, navega a src.
    static onTaponClick(clickedIndex, src, type) {
        if (this._taponSelected) return;
        this._taponSelected = true;

        // Setteja el tapon del character (helmet) amb el color/llums del tipus clicat
        if (type) Scene.setTaponTypesCrossFade(type, type, 0);

        const other = clickedIndex === 0 ? 1 : 0; // 2 tapones

        // Header no clicable durant l'animació (es reactiva a l'afterShow de la pàgina destí)
        Header.disablePointer();

        // t=0: hide del no-clicat (X de sortida 0.25) + trigger de l'animació general.
        //      En acabar → changePage sense delay extra.
        this.hideTaponAnimated(other, TAPON_HIDE_SETTINGS.outXFactorOther);
        ScrollItem__WidgetHome.hideButton(other);
        Scene.animateGeneral(() => { if (src) ControllerPage.changePage(src); });

        // +0.3s: hide del clicat (X de sortida 0.1, més cap al centre) + hide del title-torcido
        gsap.delayedCall(0.3, () => {
            this.hideTaponAnimated(clickedIndex, TAPON_HIDE_SETTINGS.outXFactorClicked);
            ScrollItem__WidgetHome.hideButton(clickedIndex);
            ScrollItem__WidgetHome.hideTitle();
        });

        // +0.8s: un cop els dos tapons ja són invisibles (fade ~0.7s), canvi de llums a
        // lateral OG (snap; el helmet centrat apareixerà més tard amb l'animació general).
        gsap.delayedCall(0.8, () => Scene.setLightsDefault());
    }

    // Hide animat d'un tapon: UN sol tween de _outProgress (0→1) mou X, Y i Z junts (paràbola
    // coherent, mateix ease que el show). El tapon passa per davant de la càmera i surt del frustum.
    // El delay/stagger el posa el caller.
    // xFactor: factor de la X de sortida (clicat → més cap al centre que el no clicat).
    static hideTaponAnimated(index, xFactor = TAPON_HIDE_SETTINGS.outXFactorOther) {
        const tapon = this._tapones[index];
        if (!tapon) return;
        this._setClickable(tapon, false);
        tapon._outXFactor = xFactor; // X de sortida (el llegeix el loop)
        gsap.killTweensOf(tapon);          // tweens d'_outProgress (i de show: _introZ/_introY/_introXFactor)
        gsap.killTweensOf(tapon._showProxy);
        gsap.to(tapon, { _outProgress: 1, duration: TAPON_HIDE_SETTINGS.duration, ease: TAPON_HIDE_SETTINGS.ease });
    }

    static loop() {
        this._tapones.forEach(tapon => {
            if (!tapon.active) return;
            const { x, y, width, height } = tapon.dom.getBoundingClientRect();
            if (width === 0 || height === 0) return;
            const pos = tapon.domPositionTo3D(x, y);
            // Posició de repòs (DOM + offsets de show)
            const baseX = (pos.x + width * 0.5) * (tapon._introXFactor ?? 1); // 0 = centre, 1 = posició final
            const baseY = (pos.y - height * 0.5) + tapon._showY + (tapon._introY || 0);
            const baseZ = TAPON_SETTINGS.zFactor * Metrics.WIDTH + (tapon._introZ || 0);
            const camZ = WebGLSketch.defaults.cameraPos.z;
            // _outProgress (0 = repòs, 1 = sortida del hide): X+Y+Z viatgen amb el MATEIX progrés.
            const t = tapon._outProgress ?? 0;
            tapon.pos.x = Maths.lerp(baseX, baseX * (tapon._outXFactor ?? TAPON_HIDE_SETTINGS.outXFactorOther), t);
            tapon.pos.y = Maths.lerp(baseY, TAPON_HIDE_SETTINGS.outYFactor * Metrics.HEIGHT, t);
            // Z final ABSOLUTA relativa a la càmera (no z_base + offset) → passa la càmera a qualsevol amplada.
            tapon.pos.z = Maths.lerp(baseZ, camZ * TAPON_HIDE_SETTINGS.outZTargetFactor, t);

            // Hide (t>0): fade out a prop de la càmera (proporcional a la seva Z) per evitar el
            // glitch de render quan el tapon s'hi acosta massa. De camZ×fadeStartFactor → camZ×fadeEndFactor.
            if (t > 0) {
                const o = Maths.clamp(Maths.map(tapon.pos.z, camZ * TAPON_HIDE_SETTINGS.fadeStartFactor, camZ * TAPON_HIDE_SETTINGS.fadeEndFactor, 1, 0), 0, 1);
                tapon.setOpacity(o);
            }

            // Rotació seguint el mouse, proporcional a la distància del mouse al CENTRE REAL
            // de l'objecte 3D (projectem la seva posició 3D a pantalla — té en compte z,
            // flotació i offset, a diferència del rect del DOM, que no es mou).
            // Rotació inicial per tapon (Y, graus) — tunejable al debug pane, en viu.
            if (tapon.object) tapon.object.scene.rotation.y = Maths.toRadians(TAPON_INIT_ROT[tapon._type] ?? 90);

            // Rotació proporcional a la DISTÀNCIA (px) del mouse al centre REAL del tapon
            // (posició 3D projectada a pantalla). graus = distància_px × intensitat (graus/px).
            // No es normalitza ni es clampa. Y ← mouseX, X ← mouseY.
            _projPos.set(tapon.pos.x, tapon.pos.y, tapon.pos.z).project(WebGLSketch.camera);
            const cx = (_projPos.x + 1) * 0.5 * Metrics.WIDTH;
            const cy = (1 - _projPos.y) * 0.5 * Metrics.HEIGHT;
            const targetRotY = Maths.toRadians((Interaction.positions.mouse.x - cx) * TAPON_MOUSE_SETTINGS.rotY);
            const targetRotX = Maths.toRadians((Interaction.positions.mouse.y - cy) * TAPON_MOUSE_SETTINGS.rotX);
            tapon.rot.x = Maths.lerp(tapon.rot.x, targetRotX, TAPON_MOUSE_SETTINGS.lerp);
            tapon.rot.y = Maths.lerp(tapon.rot.y, targetRotY, TAPON_MOUSE_SETTINGS.lerp);

            tapon.update();
        });
    }

    // Apunta les llums de cada família al CENTRE del seu tapon (un sol cop, no cada frame):
    // la flotació/rotació del tapon ja fa canviar els reflexos. Es crida a showTapon i resize.
    static updateLightTargets() {
        const center = (tapon) => {
            if (!tapon?.active) return null;
            const { x, y, width, height } = tapon.dom.getBoundingClientRect();
            if (!width || !height) return null;
            const pos = tapon.domPositionTo3D(x, y);
            return { x: pos.x + width * 0.5, y: pos.y - height * 0.5, z: TAPON_SETTINGS.zFactor * Metrics.WIDTH };
        };
        Scene.updateFamilyLightTargets({
            luminaris: center(this._taponByType.luminaris),
            obscurios: center(this._taponByType.obscurios),
        });
    }

    // Debug: log dels layers dels meshes de cada tapon (esperat: luminaris→16, obscurios→32).
    static debugLogTaponLayers() {
        console.group('[HomeScene] Tapon layers');
        this._tapones.forEach((t, i) => {
            const masks = [];
            t.traverse(o => { if (o.isMesh && o.visible) masks.push(o.layers.mask); });
            console.log(`  tapon[${i}] type=${t._type} → masks: ${masks.join(',')} (esperat lum=16 / obs=32)`);
        });
        console.groupEnd();
    }

    static resize() {
        this._tapones.forEach(tapon => { if (tapon.active) tapon.resize(); });
        this.updateLightTargets(); // re-apunta les llums al nou centre dels tapons
    }

    static dispose() {
        this._tapones.forEach(tapon => {
            gsap.killTweensOf(tapon._showProxy);
            gsap.killTweensOf(tapon); // tweens d'_introZ / _introY / _introXFactor (show + hide)
            if (tapon._proxy) WebGLSketch.removeInteractive(tapon._proxy);
            this.group.remove(tapon);
            tapon.dispose();
        });
        this._tapones = [];
        WebGLSketch.scene.remove(this.group);
    }
}
